import { create } from 'zustand';
import { Recipe, Meal } from '../types/recipe';
import { supabase } from '../lib/supabase';

interface RecipeStore {
  recipes: Recipe[];
  meals: Meal[];
  loading: boolean;
  error: string | null;
  defaultGrocerId: string | null;
  fetchRecipes: () => Promise<void>;
  fetchMeals: () => Promise<void>;
  addRecipe: (recipe: Omit<Recipe, 'id' | 'user_id'>) => Promise<void>;
  updateRecipe: (recipe: Recipe) => Promise<void>;
  deleteRecipe: (id: string) => Promise<void>;
  addMeal: (meal: { name: string; recipeIds: string[]; notes?: string }) => Promise<void>;
  updateMeal: (meal: Meal) => Promise<void>;
  deleteMeal: (id: string) => Promise<void>;
  getRecipesByMealId: (mealId: string) => Recipe[];
  setDefaultGrocer: (grocerId: string) => void;
  clearStore: () => void;
}

export const useRecipeStore = create<RecipeStore>((set, get) => ({
  recipes: [],
  meals: [],
  loading: false,
  error: null,
  defaultGrocerId: localStorage.getItem('defaultGrocerId'),

  clearStore: () => {
    set({ recipes: [], meals: [], error: null });
  },

  fetchRecipes: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ recipes: [] });
        return;
      }

      const { data, error } = await supabase
        .from('recipes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ recipes: data || [] });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch recipes' });
    } finally {
      set({ loading: false });
    }
  },

  fetchMeals: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ meals: [] });
        return;
      }

      const { data: meals, error: mealsError } = await supabase
        .from('meals')
        .select(`
          *,
          meal_recipes (
            recipe_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (mealsError) throw mealsError;

      const formattedMeals: Meal[] = (meals || []).map(meal => ({
        id: meal.id,
        name: meal.name,
        notes: meal.notes,
        recipeIds: meal.meal_recipes.map(mr => mr.recipe_id)
      }));

      set({ meals: formattedMeals });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch meals' });
    } finally {
      set({ loading: false });
    }
  },

  addRecipe: async (recipe) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Ensure ingredients are properly structured before saving
      const sanitizedIngredients = recipe.ingredients.map(ing => ({
        ...ing,
        alternatives: Array.isArray(ing.alternatives) ? ing.alternatives : [],
        category: ing.category || 'pantry-staples'
      }));

      const { data, error } = await supabase
        .from('recipes')
        .insert([{ 
          ...recipe,
          ingredients: sanitizedIngredients,
          user_id: user.id 
        }])
        .select()
        .single();

      if (error) throw error;
      set(state => ({ recipes: [data, ...state.recipes] }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add recipe' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateRecipe: async (recipe) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('recipes')
        .update({
          title: recipe.title,
          ingredients: recipe.ingredients,
          instructions: recipe.instructions,
          source_url: recipe.source_url,
          notes: recipe.notes,
          tags: recipe.tags || [],
          image_url: recipe.image_url
        })
        .eq('id', recipe.id)
        .eq('user_id', user.id);

      if (error) throw error;
      
      set(state => ({
        recipes: state.recipes.map(r => r.id === recipe.id ? recipe : r)
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update recipe' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteRecipe: async (id) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      set(state => ({
        recipes: state.recipes.filter(r => r.id !== id),
        meals: state.meals.map(meal => ({
          ...meal,
          recipeIds: meal.recipeIds.filter(rid => rid !== id)
        }))
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete recipe' });
    } finally {
      set({ loading: false });
    }
  },

  addMeal: async ({ name, recipeIds, notes }) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: meal, error: mealError } = await supabase
        .from('meals')
        .insert([{ name, notes, user_id: user.id }])
        .select()
        .single();

      if (mealError) throw mealError;

      const mealRecipes = recipeIds.map(recipeId => ({
        meal_id: meal.id,
        recipe_id: recipeId
      }));

      const { error: relationError } = await supabase
        .from('meal_recipes')
        .insert(mealRecipes);

      if (relationError) throw relationError;

      const newMeal: Meal = {
        id: meal.id,
        name: meal.name,
        notes: meal.notes,
        recipeIds
      };

      set(state => ({ meals: [newMeal, ...state.meals] }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add meal' });
    } finally {
      set({ loading: false });
    }
  },

  updateMeal: async (meal) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: mealError } = await supabase
        .from('meals')
        .update({ name: meal.name, notes: meal.notes })
        .eq('id', meal.id)
        .eq('user_id', user.id);

      if (mealError) throw mealError;

      const { error: deleteError } = await supabase
        .from('meal_recipes')
        .delete()
        .eq('meal_id', meal.id);

      if (deleteError) throw deleteError;

      const mealRecipes = meal.recipeIds.map(recipeId => ({
        meal_id: meal.id,
        recipe_id: recipeId
      }));

      const { error: insertError } = await supabase
        .from('meal_recipes')
        .insert(mealRecipes);

      if (insertError) throw insertError;

      set(state => ({
        meals: state.meals.map(m => m.id === meal.id ? meal : m)
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update meal' });
    } finally {
      set({ loading: false });
    }
  },

  deleteMeal: async (id) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('meals')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      set(state => ({
        meals: state.meals.filter(m => m.id !== id)
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete meal' });
    } finally {
      set({ loading: false });
    }
  },

  getRecipesByMealId: (mealId: string) => {
    const meal = get().meals.find(m => m.id === mealId);
    if (!meal) return [];
    return get().recipes.filter(recipe => meal.recipeIds.includes(recipe.id));
  },

  setDefaultGrocer: (grocerId: string) => {
    localStorage.setItem('defaultGrocerId', grocerId);
    set({ defaultGrocerId: grocerId });
  }
}));